import React, { useState } from 'react';
import styled from 'styled-components';
import { Badge, Button, Input, Text } from '@teals/ui-web';
import type { Agent } from '../../store/api/agentsApi';
import {
  useCreateTeamRoleMutation,
  useDeleteTeamRoleMutation,
  useSetMemberTeamRolesMutation,
  useUpdateTeamRoleMutation,
} from '../../store/api/teamsApi';
import type { TeamRoleDefinition, TeamStatement } from '../../store/api/teamsApi';
import {
  AGENT_ACCESS_LABELS,
  AGENT_ACCESS_OPTIONS,
  ALL_AGENTS,
  describeRoleGrants,
  roleAccessLevel,
  withRoleAccess,
} from './teamAccess';
import type { AgentAccessLevel } from './teamAccess';

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--border);
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Muted = styled.span`
  color: var(--text-muted);
  font-size: 0.8125rem;
`;

const Note = styled.p`
  margin: 0;
  padding: 0.75rem;
  border-radius: 8px;
  background: var(--surface-muted, rgba(0, 0, 0, 0.03));
  color: var(--text-muted);
  font-size: 0.8125rem;
  line-height: 1.4;
`;

const Select = styled.select`
  padding: 0.375rem 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text);
  font-size: 0.8125rem;
`;

const Editor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const Picker = styled.details`
  font-size: 0.8125rem;

  summary {
    cursor: pointer;
    color: var(--text-muted);
  }
`;

const PickerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding-top: 0.5rem;
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

type Run = (action: () => Promise<unknown>, success?: string) => Promise<void>;

interface RoleEditorProps {
  role: TeamRoleDefinition | null;
  agents: Agent[];
  saving: boolean;
  onCancel: () => void;
  onSave: (value: { name: string; description: string; grants: TeamStatement[] }) => void;
}

const RoleEditor: React.FC<RoleEditorProps> = ({ role, agents, saving, onCancel, onSave }) => {
  const [name, setName] = useState(role?.name || '');
  const [description, setDescription] = useState(role?.description || '');
  const [grants, setGrants] = useState<TeamStatement[]>(role?.grants || []);
  const rows = [
    { id: ALL_AGENTS, label: 'All organisation agents', hint: 'Including agents built later' },
    ...agents.map((a) => ({ id: a.id, label: a.name || a.agentName, hint: a.agentName })),
  ];

  return (
    <Editor aria-label={role ? `Edit ${role.name}` : 'New team role'}>
      <Input
        label="Role name"
        value={name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
        placeholder="e.g. Agent builders"
        fullWidth
      />
      <Input
        label="Role description"
        value={description}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
        placeholder="What members with this role do"
        fullWidth
      />
      {rows.map((row) => {
        const level = roleAccessLevel(grants, row.id);
        return (
          <Row key={row.id}>
            <RowText>
              <Text variant="body2" style={{ fontWeight: 500 }}>
                {row.label}
              </Text>
              <Muted>{row.hint}</Muted>
            </RowText>
            <Select
              value={level}
              aria-label={`Role access to ${row.label}`}
              onChange={(e) =>
                setGrants((current) =>
                  withRoleAccess(current, row.id, e.target.value as AgentAccessLevel),
                )
              }
            >
              {level === 'custom' && (
                <option value="custom">{AGENT_ACCESS_LABELS.custom}</option>
              )}
              {AGENT_ACCESS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Row>
        );
      })}
      <Actions>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={saving || !name.trim()}
          onClick={() => onSave({ name: name.trim(), description: description.trim(), grants })}
        >
          {saving ? 'Saving…' : role ? 'Save role' : 'Add role'}
        </Button>
      </Actions>
    </Editor>
  );
};

interface TeamRolesSectionProps {
  orgId: string;
  teamId: string;
  roles: TeamRoleDefinition[];
  agents: Agent[];
  run: Run;
}

/** Roles defined in one team. Members with a role get its agent access on top of the team's. */
export const TeamRolesSection: React.FC<TeamRolesSectionProps> = ({
  orgId,
  teamId,
  roles,
  agents,
  run,
}) => {
  const [editing, setEditing] = useState<TeamRoleDefinition | 'new' | null>(null);
  const [createRole, { isLoading: creating }] = useCreateTeamRoleMutation();
  const [updateRole, { isLoading: updating }] = useUpdateTeamRoleMutation();
  const [deleteRole] = useDeleteTeamRoleMutation();

  const save = (value: { name: string; description: string; grants: TeamStatement[] }) => {
    const body = {
      orgId,
      teamId,
      name: value.name,
      description: value.description || undefined,
      grants: value.grants,
    };
    const action =
      editing === 'new' || editing === null
        ? () => createRole(body).unwrap()
        : () => updateRole({ ...body, roleId: editing.id }).unwrap();
    return run(async () => {
      await action();
      setEditing(null);
    }, editing === 'new' ? 'Team role added' : 'Team role saved');
  };

  const remove = (role: TeamRoleDefinition) => {
    if (!window.confirm(`Delete role "${role.name}"? Members lose what it grants.`)) return;
    run(() => deleteRole({ orgId, teamId, roleId: role.id }).unwrap(), 'Team role deleted');
  };

  return (
    <Section aria-label="Team roles">
      <SectionTitle>Roles ({roles.length})</SectionTitle>
      <Note>
        A role gives the members who hold it agent access on top of this team&apos;s. A deny
        always wins: when a member&apos;s org role marks agents Not allowed, no team role can
        give them back. Org owners and admins are never narrowed by a team deny.
      </Note>
      {roles.map((role) =>
        editing !== 'new' && editing?.id === role.id ? (
          <RoleEditor
            key={role.id}
            role={role}
            agents={agents}
            saving={updating}
            onCancel={() => setEditing(null)}
            onSave={save}
          />
        ) : (
          <Row key={role.id}>
            <RowText>
              <Text variant="body2" style={{ fontWeight: 500 }}>
                {role.name}
              </Text>
              <Muted>
                {describeRoleGrants(role.grants)} · {role.memberCount}{' '}
                {role.memberCount === 1 ? 'member' : 'members'}
              </Muted>
            </RowText>
            <RowActions>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit role ${role.name}`}
                onClick={() => setEditing(role)}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete role ${role.name}`}
                onClick={() => remove(role)}
              >
                Delete
              </Button>
            </RowActions>
          </Row>
        ),
      )}
      {editing === 'new' ? (
        <RoleEditor
          role={null}
          agents={agents}
          saving={creating}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      ) : (
        <Actions>
          <Button variant="secondary" size="sm" onClick={() => setEditing('new')}>
            + Add role
          </Button>
        </Actions>
      )}
    </Section>
  );
};

interface MemberRolesPickerProps {
  orgId: string;
  teamId: string;
  memberId: string;
  label: string;
  roles: TeamRoleDefinition[];
  held: string[];
  run: Run;
}

/** Checkbox list of a team's roles for one member; saves on each change. */
export const MemberRolesPicker: React.FC<MemberRolesPickerProps> = ({
  orgId,
  teamId,
  memberId,
  label,
  roles,
  held,
  run,
}) => {
  const [setRoles, { isLoading }] = useSetMemberTeamRolesMutation();
  const names = roles.filter((r) => held.includes(r.id)).map((r) => r.name);

  const toggle = (roleId: string) => {
    const next = held.includes(roleId) ? held.filter((id) => id !== roleId) : [...held, roleId];
    run(
      () => setRoles({ orgId, teamId, memberId, roleIds: next }).unwrap(),
      `Roles for ${label} updated`,
    );
  };

  return (
    <Picker>
      <summary aria-label={`Team roles for ${label}`}>
        {names.length > 0 ? (
          names.map((name) => (
            <Badge key={name} variant="neutral" size="sm" style={{ marginRight: 4 }}>
              {name}
            </Badge>
          ))
        ) : (
          'No roles'
        )}
      </summary>
      <PickerList role="group" aria-label={`Roles held by ${label}`}>
        {roles.map((role) => (
          <CheckRow key={role.id}>
            <input
              type="checkbox"
              checked={held.includes(role.id)}
              disabled={isLoading}
              onChange={() => toggle(role.id)}
            />
            {role.name}
          </CheckRow>
        ))}
      </PickerList>
    </Picker>
  );
};
